// src/components/settings/surfox-settings/sections/OutboundWebhooksSection.jsx
//
// Outbound Webhooks settings panel.
//
// SCOPE
//   Manages integration_connections of types: highlevel, salesforce, podio,
//   bullhorn, generic_webhook. The legacy synchronous Zapier / RecruiterFlow
//   pipeline lives in the separate IntegrationsSection.jsx and is NOT
//   surfaced here.
//
// BACKING API
//   Talks to /api/integrations (CRUD + /test + /recent-deliveries) — see
//   src/api_routes/integrations.js. Browser direct supabase access is
//   intentionally NOT used here so credential masking and validation stay
//   server-side.
//
// VISUAL STYLE
//   Mirrors IntegrationsSection.jsx — slate/orange palette, lucide-react,
//   modal-overlay pattern, EyeOff/Eye for secret reveal.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plug,
  Plus,
  Trash2,
  AlertCircle,
  Eye,
  EyeOff,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Send,
  Edit2,
  X,
} from 'lucide-react';

// ----------------------------------------------------------------------------
// Constants — must align with src/api_routes/integrations.js validation
// ----------------------------------------------------------------------------
const INTEGRATION_TYPES = [
  { id: 'highlevel',       label: 'HighLevel' },
  { id: 'salesforce',      label: 'Salesforce' },
  { id: 'podio',           label: 'Podio' },
  { id: 'bullhorn',        label: 'Bullhorn' },
  { id: 'generic_webhook', label: 'Generic Webhook' },
];

const AVAILABLE_EVENTS = [
  { id: 'lead_turned_hot', label: 'Lead Turned Hot', description: 'Fires when a lead reaches Hot status (score-driven, judge-driven, or manual).' },
];

const AUTH_TYPES = [
  { id: 'none',   label: 'No auth' },
  { id: 'bearer', label: 'Bearer token' },
  { id: 'header', label: 'Custom header' },
];

// ----------------------------------------------------------------------------
// Small fetch helper. Auth pattern matches the rest of the codebase:
// Bearer token from localStorage (set by AuthContext on login).
// ----------------------------------------------------------------------------
async function api(path, options = {}) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const res = await fetch(path, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
    ...options,
  });
  if (res.status === 204) return null;
  const text = await res.text();
  let body;
  try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }
  if (!res.ok) {
    const message = body?.error || `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

// ============================================================================
// CONNECTION FORM MODAL — used for both Create and Edit
// ============================================================================
function ConnectionFormModal({ isOpen, mode, initial, onClose, onSubmit }) {
  // mode = 'create' | 'edit'
  const [integrationType, setIntegrationType] = useState('highlevel');
  const [integrationName, setIntegrationName] = useState('');
  const [webhookUrl, setWebhookUrl]           = useState('');
  const [authType, setAuthType]               = useState('none');
  const [bearerToken, setBearerToken]         = useState('');
  const [headerName, setHeaderName]           = useState('');
  const [headerValue, setHeaderValue]         = useState('');
  const [selectedEvents, setSelectedEvents]   = useState(['lead_turned_hot']);
  const [showSecret, setShowSecret]           = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && initial) {
      setIntegrationType(initial.integration_type || 'highlevel');
      setIntegrationName(initial.integration_name || '');
      setWebhookUrl(initial.webhook_url || '');
      setAuthType(initial.credentials?.auth_type || 'none');
      setBearerToken('');   // never pre-fill secrets — masked from API
      setHeaderName(initial.credentials?.name || '');
      setHeaderValue('');
      setSelectedEvents(initial.subscribed_events?.length ? initial.subscribed_events : ['lead_turned_hot']);
    } else {
      setIntegrationType('highlevel');
      setIntegrationName('');
      setWebhookUrl('');
      setAuthType('none');
      setBearerToken('');
      setHeaderName('');
      setHeaderValue('');
      setSelectedEvents(['lead_turned_hot']);
    }
    setShowSecret(false);
    setError('');
  }, [isOpen, mode, initial]);

  if (!isOpen) return null;

  const buildCredentials = () => {
    if (authType === 'none')   return { auth_type: 'none' };
    if (authType === 'bearer') return { auth_type: 'bearer', token: bearerToken };
    if (authType === 'header') return { auth_type: 'header', name: headerName, value: headerValue };
    return { auth_type: 'none' };
  };

  const handleSubmit = async () => {
    setError('');

    if (!webhookUrl.startsWith('https://')) {
      setError('Webhook URL must start with https://');
      return;
    }
    if (!integrationName.trim()) {
      setError('Connection name is required');
      return;
    }
    if (selectedEvents.length === 0) {
      setError('Select at least one event');
      return;
    }
    if (authType === 'bearer' && !bearerToken && mode === 'create') {
      setError('Bearer token is required');
      return;
    }
    if (authType === 'header' && (!headerName || (!headerValue && mode === 'create'))) {
      setError('Header name and value are required');
      return;
    }

    // For edit mode: only include credentials if the user re-entered a secret.
    // This lets them update name/url/events without retyping the bearer token.
    let payload = {
      integration_type: integrationType,
      integration_name: integrationName.trim(),
      webhook_url:      webhookUrl,
      subscribed_events: selectedEvents,
    };
    if (mode === 'create') {
      payload.credentials = buildCredentials();
    } else {
      // edit: only send credentials if user actually filled them (or switched auth type)
      const switchedAuthType = (initial?.credentials?.auth_type || 'none') !== authType;
      const enteredNewSecret =
        (authType === 'bearer' && bearerToken) ||
        (authType === 'header' && (headerValue || headerName !== (initial?.credentials?.name || '')));
      if (switchedAuthType || enteredNewSecret || authType === 'none') {
        payload.credentials = buildCredentials();
      }
    }

    setSaving(true);
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Plug className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {mode === 'create' ? 'Add Outbound Webhook' : 'Edit Connection'}
              </h3>
              <p className="text-sm text-slate-500">Send hot-lead events to your CRM or automation tool</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Integration type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Integration type <span className="text-red-500">*</span></label>
            <select
              value={integrationType}
              onChange={(e) => setIntegrationType(e.target.value)}
              disabled={mode === 'edit'}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-50 disabled:text-slate-500"
            >
              {INTEGRATION_TYPES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {mode === 'edit' && (
              <p className="text-xs text-slate-500 mt-1">Type can't be changed after creation. Delete and re-create to switch.</p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Connection name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={integrationName}
              onChange={(e) => setIntegrationName(e.target.value)}
              placeholder="e.g., Sales CRM (HighLevel)"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Webhook URL */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Webhook URL <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://hooks.example.com/path"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-slate-500 mt-1">Must use https.</p>
          </div>

          {/* Auth type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Authentication</label>
            <select
              value={authType}
              onChange={(e) => setAuthType(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {AUTH_TYPES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Bearer token field */}
          {authType === 'bearer' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Bearer token {mode === 'create' && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={bearerToken}
                  onChange={(e) => setBearerToken(e.target.value)}
                  placeholder={mode === 'edit' ? 'Leave blank to keep existing token' : 'Enter your token'}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Sent as <code className="font-mono">Authorization: Bearer …</code></p>
            </div>
          )}

          {/* Custom header fields */}
          {authType === 'header' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Header name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={headerName}
                  onChange={(e) => setHeaderName(e.target.value)}
                  placeholder="e.g., X-API-Key"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Header value {mode === 'create' && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={headerValue}
                    onChange={(e) => setHeaderValue(e.target.value)}
                    placeholder={mode === 'edit' ? 'Leave blank to keep existing value' : 'Header value'}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Subscribed events */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Events <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              {AVAILABLE_EVENTS.map((ev) => (
                <label
                  key={ev.id}
                  className={`
                    flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors
                    ${selectedEvents.includes(ev.id)
                      ? 'border-orange-300 bg-orange-50'
                      : 'border-slate-200 hover:border-slate-300'}
                  `}
                >
                  <input
                    type="checkbox"
                    checked={selectedEvents.includes(ev.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedEvents([...selectedEvents, ev.id]);
                      else                  setSelectedEvents(selectedEvents.filter(x => x !== ev.id));
                    }}
                    className="mt-0.5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                  />
                  <div>
                    <span className="font-medium text-slate-900 text-sm">{ev.label}</span>
                    <p className="text-xs text-slate-500 mt-0.5">{ev.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === 'create' ? 'Add Connection' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CONNECTION CARD — list item with Test, Edit, Delete, Recent Deliveries
// ============================================================================
function ConnectionCard({ connection, onEdit, onDelete, onToggleEnabled }) {
  const [testRunning, setTestRunning]       = useState(false);
  const [testResult, setTestResult]         = useState(null);
  const [deliveriesOpen, setDeliveriesOpen] = useState(false);
  const [deliveries, setDeliveries]         = useState(null);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  const runTest = async () => {
    setTestRunning(true);
    setTestResult(null);
    try {
      const res = await api(`/api/integrations/${connection.id}/test`, { method: 'POST' });
      setTestResult(res);
    } catch (err) {
      setTestResult({ success: false, error_message: err.message });
    } finally {
      setTestRunning(false);
    }
  };

  const toggleDeliveries = async () => {
    const next = !deliveriesOpen;
    setDeliveriesOpen(next);
    if (next && deliveries === null) {
      await loadDeliveries();
    }
  };

  const loadDeliveries = async () => {
    setLoadingDeliveries(true);
    try {
      const res = await api(`/api/integrations/${connection.id}/recent-deliveries`);
      setDeliveries(res.deliveries || []);
    } catch (err) {
      setDeliveries([]);
      console.error('Failed to load deliveries', err);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2 flex-wrap gap-y-1">
              <h4 className="font-semibold text-slate-900 truncate">{connection.integration_name}</h4>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                {INTEGRATION_TYPES.find(t => t.id === connection.integration_type)?.label || connection.integration_type}
              </span>
              {connection.enabled ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <CheckCircle className="w-3 h-3 mr-1" /> Enabled
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Disabled</span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1 font-mono break-all">{connection.webhook_url}</p>
            <p className="text-xs text-slate-400 mt-1">
              Triggers: {connection.subscribed_events?.join(', ') || '—'}
            </p>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <button
              onClick={() => onToggleEnabled(connection.id, !connection.enabled)}
              className="p-2 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              title={connection.enabled ? 'Disable' : 'Enable'}
            >
              {connection.enabled ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              onClick={() => onEdit(connection)}
              className="p-2 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(connection)}
              className="p-2 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center space-x-2">
          <button
            onClick={runTest}
            disabled={testRunning}
            className="px-3 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50 flex items-center"
          >
            {testRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Test
          </button>
          <button
            onClick={toggleDeliveries}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center"
          >
            {deliveriesOpen ? <ChevronDown className="w-4 h-4 mr-2" /> : <ChevronRight className="w-4 h-4 mr-2" />}
            Recent deliveries
          </button>
        </div>

        {testResult && (
          <div className={`mt-3 p-3 rounded-lg border text-sm ${
            testResult.success
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="font-medium">
              {testResult.success ? `✓ Success (HTTP ${testResult.status_code})` : `✗ Failed${testResult.status_code ? ` (HTTP ${testResult.status_code})` : ''}`}
            </div>
            {testResult.error_message && (
              <div className="text-xs mt-1 break-all">{testResult.error_message}</div>
            )}
            {testResult.response_body && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs font-medium">Response body</summary>
                <pre className="mt-1 text-xs whitespace-pre-wrap break-all bg-white/60 p-2 rounded border border-current/10">{testResult.response_body}</pre>
              </details>
            )}
          </div>
        )}
      </div>

      {deliveriesOpen && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/50">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Last 50 deliveries</h5>
            <button
              onClick={loadDeliveries}
              disabled={loadingDeliveries}
              className="p-1 text-slate-400 hover:text-slate-700 rounded"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingDeliveries ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {loadingDeliveries && deliveries === null ? (
            <p className="text-sm text-slate-500 text-center py-4">Loading…</p>
          ) : deliveries && deliveries.length > 0 ? (
            <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
              {deliveries.map((d) => {
                const isTest = typeof d.event_type === 'string' && d.event_type.endsWith('.test');
                return (
                  <div
                    key={d.id}
                    className={`flex items-center justify-between text-xs p-2 rounded border ${
                      d.success
                        ? 'bg-white border-emerald-100'
                        : 'bg-white border-red-100'
                    }`}
                  >
                    <div className="flex items-center space-x-2 min-w-0">
                      <span className={d.success ? 'text-emerald-600' : 'text-red-600'}>
                        {d.success ? '✓' : '✗'}
                      </span>
                      <span className="font-mono text-slate-700">
                        {d.status_code != null ? `HTTP ${d.status_code}` : '—'}
                      </span>
                      {isTest && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 uppercase tracking-wide">
                          test
                        </span>
                      )}
                      {d.error_message && (
                        <span className="text-slate-500 truncate" title={d.error_message}>
                          {d.error_message}
                        </span>
                      )}
                    </div>
                    <span className="text-slate-400 flex-shrink-0 ml-2">
                      {new Date(d.attempted_at).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">No deliveries yet</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN SECTION
// ============================================================================
export default function OutboundWebhooksSection({ onUpdate }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const [formOpen, setFormOpen]   = useState(false);
  const [formMode, setFormMode]   = useState('create');
  const [formInitial, setFormInitial] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchConnections = useCallback(async () => {
    setError('');
    try {
      const res = await api('/api/integrations');
      setConnections(res.connections || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleCreate = () => {
    setFormMode('create');
    setFormInitial(null);
    setFormOpen(true);
  };

  const handleEdit = (connection) => {
    setFormMode('edit');
    setFormInitial(connection);
    setFormOpen(true);
  };

  const handleSubmit = async (payload) => {
    if (formMode === 'create') {
      await api('/api/integrations', { method: 'POST', body: JSON.stringify(payload) });
    } else {
      await api(`/api/integrations/${formInitial.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    }
    await fetchConnections();
    onUpdate?.();
  };

  const handleToggleEnabled = async (id, enabled) => {
    try {
      await api(`/api/integrations/${id}`, { method: 'PATCH', body: JSON.stringify({ enabled }) });
      await fetchConnections();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteConfirmed = async () => {
    const id = confirmDelete?.id;
    if (!id) return;
    try {
      await api(`/api/integrations/${id}`, { method: 'DELETE' });
      setConfirmDelete(null);
      await fetchConnections();
      onUpdate?.();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-100 rounded-xl">
        <div className="flex items-start space-x-3">
          <Plug className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-orange-900">Outbound webhooks</h4>
            <p className="text-sm text-orange-800 mt-1">
              Send hot-lead events to HighLevel, Salesforce, Podio, Bullhorn, or any custom webhook URL.
              Deliveries are queued and retried with exponential backoff up to 5 attempts.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Add button */}
      <div className="flex justify-end">
        <button
          onClick={handleCreate}
          className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg"
        >
          <Plus className="w-4 h-4" />
          <span>Add Connection</span>
        </button>
      </div>

      {/* List */}
      {connections.length === 0 ? (
        <div className="text-center py-12 px-4 border-2 border-dashed border-slate-200 rounded-xl">
          <Plug className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-1">No outbound webhooks yet</p>
          <p className="text-xs text-slate-400">Add a connection to start delivering hot-lead events to your destination.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {connections.map((c) => (
            <ConnectionCard
              key={c.id}
              connection={c}
              onEdit={handleEdit}
              onDelete={(conn) => setConfirmDelete(conn)}
              onToggleEnabled={handleToggleEnabled}
            />
          ))}
        </div>
      )}

      {/* Form modal */}
      <ConnectionFormModal
        isOpen={formOpen}
        mode={formMode}
        initial={formInitial}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900">Delete connection?</h3>
            <p className="text-sm text-slate-600 mt-2">
              <strong>{confirmDelete.integration_name}</strong> will be permanently removed.
              Pending deliveries to this destination will be marked dead-letter on the next dispatcher pass.
            </p>
            <div className="mt-5 flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirmed}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
